import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/api';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const ThemeContext = createContext();

// Default theme values
const defaultTheme = {
  clubCode: 'TEAM',
  displayName: 'Team Central',
  primaryColor: '#1e3a8a',
  secondaryColor: '#eab308',
  logoUrl: null,
  weatherLat: null,
  weatherLon: null,
  weatherLocationName: null,
  usgsSiteId: null,
  usgsSiteName: null
};

// Helper to convert hex to RGB for CSS variable usage
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
    : null;
};

// Mix toward white (positive percent) or black (negative) so tints keep the source hue.
const adjustColor = (hex, percent) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return hex;

  const mix = Math.min(100, Math.max(-100, percent)) / 100;
  const channel = (value) => {
    const next = mix >= 0
      ? value + (255 - value) * mix
      : value * (1 + mix);
    return Math.round(Math.min(255, Math.max(0, next)));
  };

  const r = channel(parseInt(result[1], 16));
  const g = channel(parseInt(result[2], 16));
  const b = channel(parseInt(result[3], 16));
  return `#${[r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [teamSettings, setTeamSettings] = useState(defaultTheme);
  const [loading, setLoading] = useState(false);

  // Apply CSS variables to document root
  const applyTheme = useCallback((settings) => {
    const root = document.documentElement;
    
    // Primary color variations
    root.style.setProperty('--color-primary', settings.primaryColor);
    root.style.setProperty('--color-primary-rgb', hexToRgb(settings.primaryColor));
    root.style.setProperty('--color-primary-light', adjustColor(settings.primaryColor, 40));
    root.style.setProperty('--color-primary-lighter', adjustColor(settings.primaryColor, 70));
    root.style.setProperty('--color-primary-dark', adjustColor(settings.primaryColor, -20));
    
    // Secondary color variations
    root.style.setProperty('--color-secondary', settings.secondaryColor);
    root.style.setProperty('--color-secondary-rgb', hexToRgb(settings.secondaryColor));
    root.style.setProperty('--color-secondary-light', adjustColor(settings.secondaryColor, 30));
    root.style.setProperty('--color-secondary-dark', adjustColor(settings.secondaryColor, -20));
    
    // Background derived from primary (very light tint)
    root.style.setProperty('--color-bg', adjustColor(settings.primaryColor, 85));
    root.style.setProperty('--color-bg-secondary', adjustColor(settings.primaryColor, 75));
  }, []);

  // Update team settings (called from AuthContext when user logs in)
  const updateTeamSettings = useCallback((settings) => {
    if (settings) {
      setTeamSettings(settings);
      applyTheme(settings);
      // Save to localStorage for persistence
      localStorage.setItem('teamSettings', JSON.stringify(settings));
    }
  }, [applyTheme]);

  // Reset to defaults (called on logout)
  const resetTheme = useCallback(() => {
    setTeamSettings(defaultTheme);
    applyTheme(defaultTheme);
    localStorage.removeItem('teamSettings');
  }, [applyTheme]);

  // Fetch team settings from API (for refreshing)
  const fetchTeamSettings = useCallback(async (token) => {
    if (!token) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/teams/settings`, {
        headers: getAuthHeaders(token)
      });

      if (response.ok) {
        const data = await response.json();
        updateTeamSettings(data.settings);
        return data.settings;
      }
    } catch (error) {
      console.error('Failed to fetch team settings:', error);
    } finally {
      setLoading(false);
    }
  }, [updateTeamSettings]);

  // Update team settings via API (admin only)
  const saveTeamSettings = useCallback(async (token, newSettings) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/teams/settings`, {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify(newSettings)
      });

      const data = await response.json();

      if (response.ok) {
        updateTeamSettings(data.settings);
        return { success: true, settings: data.settings };
      } else {
        return { success: false, error: data.error || 'Failed to update settings' };
      }
    } catch (error) {
      console.error('Failed to save team settings:', error);
      return { success: false, error: 'Network error' };
    } finally {
      setLoading(false);
    }
  }, [updateTeamSettings]);

  // Load saved theme from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('teamSettings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setTeamSettings(parsed);
        applyTheme(parsed);
      } catch (e) {
        console.error('Failed to parse saved team settings:', e);
      }
    } else {
      // Apply default theme
      applyTheme(defaultTheme);
    }
  }, [applyTheme]);

  const value = {
    teamSettings,
    loading,
    updateTeamSettings,
    resetTheme,
    fetchTeamSettings,
    saveTeamSettings
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

